import {
  ApiError,
  CenteredLoader,
  ChartCoordinates,
  LineChart,
  LineChartDataset,
  LineChartProps,
  TooltipItem,
  TooltipModel,
  useTheme,
} from "@shared/primitive-components"
import { lightenColor } from "@shared/primitive-components/src/utils/lightenDarken"
import formatMoney from "@shared/utils/formatters/formatMoney"
import { useMemo } from "react"
import { RebateSummaryPeriodData } from "../../../api/rebates/useGetRebateSummary/types"
import { usePolicyAndFleet } from "../../../contexts"
import { reduceDateByOneHour, toChartDate } from "../../../utils"
import { useRebateSummary } from "../../../api/rebates/useGetRebateSummary"

export const REBATES_SUMMARY_CHART_DTI = `rebates_summary_chart`

type Props = {
  periods: ReadonlyArray<RebateSummaryPeriodData>
}

const Chart = ({ periods }: Props) => {
  const theme = useTheme()
  const { policy } = usePolicyAndFleet()
  const { startTime } = policy

  const chart = useMemo<LineChartProps<ChartCoordinates<number | null>[], string>>(() => {
    // Build datasets
    const labels = [toChartDate(startTime), ...periods.map((period) => toChartDate(reduceDateByOneHour(period.date)))]
    const periodsDatesets = periods.map((it, i) => ({
      x: labels[i + 1],
      y: it.accumulatedRebate,
    }))
    periodsDatesets.unshift({ x: toChartDate(startTime), y: 0 })

    const datasets: LineChartDataset<ChartCoordinates<number | null>[]> = [
      {
        data: periodsDatesets,
        useGradient: true,
        datasetOptions: {
          order: 1,
          borderColor: theme.palette.brand,
          pointRadius: 4,
        },
      },
    ]

    // Build tooltips
    const toolTipLabelFn = function (this: TooltipModel<"line">, tooltipItem: TooltipItem<"line">) {
      const { raw } = tooltipItem
      const coordinate = raw as ChartCoordinates<number>
      return formatMoney(coordinate.y)
    }

    const toolTipTitleFn = function (this: TooltipModel<"line">, tooltipItem: TooltipItem<"line">[]) {
      const item = tooltipItem[0]
      const { raw } = item
      const { x } = raw as ChartCoordinates<number>
      return x
    }

    // Instantiate chart details
    const yTickFn = (tickValue: number): string | undefined => (tickValue ? formatMoney(tickValue) : undefined)

    const yAxisMaxValue = periods.length && periods[0].accumulatedRebate !== null ? undefined : 100

    return {
      datasets,
      gradientColor: lightenColor(theme.palette.brand, 100),
      height: 360,
      labels,
      maintainAspectRatio: false,
      responsive: true,
      toolTipLabelFn,
      toolTipTitleFn,
      useGradient: true,
      yAxisMaxValue,
      yTickFn,
    }
  }, [periods, startTime, theme.palette.brand])
  return <LineChart<ChartCoordinates<number | null>[], string> chart={chart} dataTestId={REBATES_SUMMARY_CHART_DTI} />
}

const RebateChart = () => {
  const { apiErrored: errRebates, data: summary, loading: rebatesLoading } = useRebateSummary()
  if (errRebates) return <ApiError />

  if (rebatesLoading) {
    return <CenteredLoader height="100%" />
  }

  return <Chart periods={summary?.periods || []} />
}

export const RebateChartComponent = <RebateChart />
